/**
 * AutomationTab.tsx — pestaña Automatización de Configuración.
 *
 * Especificación visual: handoff/automatizacion.html (2026-08-29). Tres
 * motores con rótulo de grupo suelto (no tarjeta): MOTOR DE PROSPECTOS ·
 * VIGILANCIA DEL CORREO · EVO BRAIN. Diez tarjetas en el orden del archivo.
 *
 * Guardado: TODO autoguarda — interruptores y selectores al cambiar,
 * campos de texto/número al perder foco o tras 800ms sin teclear. La
 * confirmación es el chip "✓ Guardado" en la cabecera de la tarjeta
 * (--evo-saved-fg, ~2s). No hay botón Save.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { Icon } from '../Icon.js';
import { Card, CardTitle } from '../ui/Card.js';
import { Pill } from '../PillGroup.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { Dropdown } from '../ui/Dropdown.js';
import { Toggle } from '../ui/Toggle.js';
import { Radio } from '../ui/Radio.js';
import { SegmentedControl } from '../ui/SegmentedControl.js';
import { SubRow } from '../ui/SubRow.js';
import { EmptyState } from '../ui/EmptyState.js';
import { LoadingState } from '../ui/LoadingState.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { useToast } from '../ui/Toast.js';
import {
  useLeadEngineSettings, useUpdateLeadEngineSettings,
  useLeadPipelines, useCreateLeadPipeline, useUpdateLeadPipeline,
  useDeleteLeadPipeline, useRunPipelineNow,
  type LeadPipeline, type LeadPipelineInput, type ScrapeZone,
} from '../../hooks/useSettings.js';
import { useAllSettings, useUpdateSetting } from '../../hooks/useSettings.js';
import { useFollowupRules, useDeleteFollowupRule, type FollowupRule } from '../../hooks/useEmail.js';
import { useMessagingAutonomy, useSetAutonomy, type AutonomyLevel } from '../../hooks/useMessagingAutonomy.js';
import { useBusinessContext } from '../../hooks/useBusinessContext.js';
import { useToolApprovals, useRevokeToolApproval } from '../../hooks/useAssistant.js';
import { useRunnerStatus, useSetRunMode } from '../../hooks/useTasks.js';
import { PipelineEditorModal, SCRAPE_CATEGORY_OPTIONS } from '../leads/PipelineEditorModal.js';
import { parseServerDate } from '../../lib/date.js';
import { useDebouncedFlush } from '../../hooks/useDebouncedFlush.js';

// ── Piezas del diseño ────────────────────────────────────────────────────────

/** Rótulo de grupo — texto suelto en caps, nunca una tarjeta. */
function GroupLabel({ children, first }: { children: ReactNode; first?: boolean }) {
  return (
    <span style={{
      marginTop: first ? 0 : 'var(--evo-space-6)',
      fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)',
      fontWeight: 'var(--evo-fw-medium)', letterSpacing: 'var(--evo-ls-caps)',
      textTransform: 'uppercase', color: 'var(--evo-fg-4)',
    }}>
      {children}
    </span>
  );
}

/** Chip "✓ Guardado" de la cabecera — la confirmación del autoguardado. */
function SavedChip({ show }: { show: boolean }) {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', color: 'var(--evo-saved-fg)', whiteSpace: 'nowrap' }}>
      <Icon name="check" size={11} />{t('settings.autoTab.savedChip')}
    </span>
  );
}

/** Cabecera de tarjeta: título caps a la izquierda + extras a la derecha. */
function CardHead({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <>
      <span style={{ flex: 1, minWidth: 0 }}><CardTitle>{title}</CardTitle></span>
      {children}
    </>
  );
}

/** Meta gris de cabecera ("2 pipelines · 1 con envío automático"). */
function HeadMeta({ children }: { children: ReactNode }) {
  return (
    <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

/** Párrafo descriptivo con que abre cada tarjeta. */
function CardIntro({ children }: { children: ReactNode }) {
  return (
    <p style={{ margin: '0 0 var(--evo-space-2)', fontSize: 'var(--evo-fs-body)', fontFamily: 'var(--evo-font)', lineHeight: 1.65, color: 'var(--evo-fg-3)' }}>
      {children}
    </p>
  );
}

/** Fila de campo del diseño: etiqueta, control (máx 560px), ayuda debajo. */
function FieldRow({ label, hint, last, children }: { label: string; hint?: string; last?: boolean; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-field-gap)', padding: 'var(--evo-field-py) 0', borderBottom: last ? 'none' : '1px solid var(--evo-stroke)' }}>
      <span style={{ fontSize: 'var(--evo-fs-label)', fontWeight: 'var(--evo-fw-medium)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap', maxWidth: 560 }}>{children}</div>
      {hint && <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', lineHeight: 1.5 }}>{hint}</span>}
    </div>
  );
}

/** Fila KV: etiqueta a ancho natural, valor a la DERECHA. Si el valor lleva
 *  flex (pill), text-align es inerte — por eso justifyContent. */
function KVRight({ label, value, dim, last }: { label: string; value: ReactNode; dim?: boolean; last?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-detail-kv-gap)', padding: 'var(--evo-detail-kv-row-py) 0', borderBottom: last ? 'none' : '1px solid var(--evo-stroke)', minHeight: 'var(--evo-detail-kv-row-min-h)', boxSizing: 'border-box' }}>
      <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-label)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)' }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 'var(--evo-space-2)', flexWrap: 'wrap', fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)', color: dim ? 'var(--evo-fg-4)' : 'var(--evo-text-strong)' }}>
        {value}
      </span>
    </div>
  );
}

/** Aviso ámbar del diseño (solape de horarios, guion sin aprobar). */
function AmberNotice({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-3)', padding: 'var(--evo-space-3) var(--evo-space-4)', marginBottom: 'var(--evo-space-4)', border: '1px solid var(--evo-amber)', borderRadius: 'var(--evo-r)', background: 'var(--evo-fill-warning)' }}>
      <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-amber)', marginTop: 1 }}><Icon name="flag" size={14} /></span>
      <span style={{ flex: 1, fontSize: 'var(--evo-fs-body)', fontFamily: 'var(--evo-font)', lineHeight: 1.6, color: 'var(--evo-fg-2)' }}>{children}</span>
      {action}
    </div>
  );
}

/** Pill xs de contorno (estados en cabeceras y filas). */
function StatusPill({ tone, children }: { tone: 'cyan' | 'amber' | 'muted' | 'info'; children: ReactNode }) {
  return <Pill size="xs" variant="outline" tone={tone}>{children}</Pill>;
}

const grid2: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--evo-related-gap)', alignItems: 'stretch',
};

/** Chip Guardado con apagado a los 2s. */
function useSavedFlag(): [boolean, () => void] {
  const [on, setOn] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return [on, () => {
    setOn(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOn(false), 2000);
  }];
}

// ── GRUPO 1 · Motor de prospectos ────────────────────────────────────────────

/** Búsqueda de prospectos — resumen de lo que buscan los pipelines activos.
 *  La edición vive en cada pipeline; "Editar" baja a esa tarjeta. */
function ProspectSearchCard() {
  const { t } = useTranslation();
  const { data: pipelines = [] } = useLeadPipelines();
  const activos = pipelines.filter(p => p.enabled === 1);
  const categorias = [...new Set(activos.flatMap(p => p.categories))];
  const zonas = [...new Set(activos.flatMap(p => p.zones.map(z => `${z.city}${z.state ? `, ${z.state}` : ''}`)))];
  const tope = activos.reduce((s, p) => s + p.capPerRun, 0);
  const intents = [...new Set(activos.map(p => (p.intent as string) || 'audit'))];

  const chip = (label: string) => (
    <span key={label} style={{ display: 'inline-flex', alignItems: 'center', height: 'var(--evo-pill-h-sm)', boxSizing: 'border-box', padding: '0 var(--evo-pill-px)', fontSize: 'var(--evo-fs-caption)', fontWeight: 'var(--evo-fw-medium)', lineHeight: 1, color: 'var(--evo-fg-2)', background: 'var(--evo-fill-subtle-strong)', border: '1px solid transparent', borderRadius: 'var(--evo-r-pill)' }}>
      {label}
    </span>
  );
  const vacio = <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)' }}>{t('settings.autoTab.search.none')}</span>;

  return (
    <Card padding="md" header={
      <CardHead title={t('settings.autoTab.cards.search')}>
        <StatusPill tone={activos.length > 0 ? 'cyan' : 'muted'}>
          {activos.length > 0 ? t('settings.pipelines.active') : t('settings.pipelines.inactive')}
        </StatusPill>
        <a href="#pipelines" style={{ flexShrink: 0, fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-cyan)', textDecoration: 'none', whiteSpace: 'nowrap' }}
          onClick={e => { e.preventDefault(); document.getElementById('pipelines-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
          {t('common.edit')}
        </a>
      </CardHead>
    }>
      <CardIntro>{t('settings.autoTab.search.intro')}</CardIntro>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FieldRow label={t('settings.scrapeSchedule.categoriesLabel')} hint={t('settings.autoTab.search.categoriesHint')}>
          {categorias.length > 0
            ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{categorias.map(c => chip(t(`leads.category.${c}`, { defaultValue: c })))}</div>
            : vacio}
        </FieldRow>
        <FieldRow label={t('settings.scrapeSchedule.zonesLabel')} hint={t('settings.autoTab.search.zonesHint')}>
          {zonas.length > 0
            ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{zonas.map(z => chip(z))}</div>
            : vacio}
        </FieldRow>
        <FieldRow label={t('settings.scrapeSchedule.capLabel')} hint={t('settings.autoTab.search.capHint')}>
          <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-text-strong)', fontFamily: 'var(--evo-font)' }}>{tope}</span>
          <HeadMeta>{t('settings.autoTab.search.capUnit')}</HeadMeta>
        </FieldRow>
        <FieldRow label={t('settings.scrapeSchedule.intentLabel')} last>
          {intents.length > 0
            ? <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-text-strong)', fontFamily: 'var(--evo-font)' }}>
                {intents.map(i => t(`settings.scrapeSchedule.intent.${i}`)).join(' · ')}
              </span>
            : vacio}
        </FieldRow>
      </div>
    </Card>
  );
}

/** Secuencia de contacto + Límites — comparten el mismo PUT de lead engine.
 *  El padre (grupo) mantiene el estado y cada tarjeta marca su propio chip. */
function useLeadEngineForm() {
  const { data: settings } = useLeadEngineSettings();
  const update = useUpdateLeadEngineSettings();
  const [form, setForm] = useState({
    followupCount: 2, followupDelay1: 3, followupDelay2: 7, followupDelay3: 14,
    signatureHtml: '', postalAddress: '', fromAccount: '', dailyCap: 10,
    autoSendStartHour: 9, autoSendEndHour: 17, autoCloseGraceDays: 2,
  });
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    if (!settings) return;
    setForm({
      followupCount: settings.followupCount,
      followupDelay1: settings.followupDelay1,
      followupDelay2: settings.followupDelay2,
      followupDelay3: settings.followupDelay3,
      signatureHtml: settings.signatureHtml,
      postalAddress: settings.postalAddress ?? '',
      fromAccount: settings.fromAccount,
      dailyCap: settings.dailyCap,
      autoSendStartHour: settings.autoSendStartHour,
      autoSendEndHour: settings.autoSendEndHour,
      autoCloseGraceDays: settings.autoCloseGraceDays ?? 2,
    });
  }, [settings]);

  return { form, setForm, formRef, update, loaded: settings != null };
}

function SequenceCard({ engine }: { engine: ReturnType<typeof useLeadEngineForm> }) {
  const { t } = useTranslation();
  const [saved, markSaved] = useSavedFlag();
  const flush = useDebouncedFlush(() => {
    engine.update.mutate(engine.formRef.current, { onSuccess: markSaved });
  });

  function patch(partial: Partial<typeof engine.form>, immediate = false) {
    engine.setForm(f => ({ ...f, ...partial }));
    if (immediate) setTimeout(() => flush.now(), 0); else flush.queue();
  }

  return (
    <Card padding="md" header={<CardHead title={t('settings.autoTab.cards.sequence')}><SavedChip show={saved} /></CardHead>}>
      <CardIntro>{t('settings.autoTab.sequence.intro')}</CardIntro>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FieldRow label={t('settings.leadEngine.followupCountLabel')} hint={t('settings.leadEngine.followupCountHint')}>
          <Dropdown<number>
            value={engine.form.followupCount}
            options={[1, 2, 3].map(n => ({ value: n, label: t('settings.leadEngine.followupCountOption', { count: n }) }))}
            onChange={v => patch({ followupCount: v }, true)}
            size="sm"
          />
        </FieldRow>
        <FieldRow label={t('settings.leadEngine.signatureLabel')} hint={t('settings.leadEngine.signatureHint')}>
          <Textarea
            value={engine.form.signatureHtml}
            onChange={v => patch({ signatureHtml: v })}
            onBlur={() => flush.now()}
            rows={4}
            resize="vertical"
            placeholder={t('settings.leadEngine.signaturePlaceholder')}
            fullWidth
          />
        </FieldRow>
        <FieldRow label={t('settings.leadEngine.postalAddressLabel')} hint={t('settings.leadEngine.postalAddressHint')}>
          <Input
            value={engine.form.postalAddress}
            onChange={v => patch({ postalAddress: v })}
            onBlur={() => flush.now()}
            placeholder={t('settings.leadEngine.postalAddressPlaceholder')}
            size="sm"
            fullWidth
          />
        </FieldRow>
        <FieldRow label={t('settings.leadEngine.autoCloseGraceDaysLabel')} hint={t('settings.leadEngine.autoCloseGraceDaysHint')} last>
          <span style={{ width: 120 }}>
            <Input type="number" min={1} max={30} value={String(engine.form.autoCloseGraceDays)}
              onChange={v => patch({ autoCloseGraceDays: Math.min(30, Math.max(1, parseInt(v) || 1)) })}
              onBlur={() => flush.now()} size="sm" fullWidth />
          </span>
          <HeadMeta>{t('settings.leadEngine.days')}</HeadMeta>
        </FieldRow>
      </div>
    </Card>
  );
}

/** Límites de envío automático — el freno, con acento ámbar cuando hay
 *  pipelines mandando solos. El aviso del tope global vive acá. */
function AutoSendLimitsCard({ engine }: { engine: ReturnType<typeof useLeadEngineForm> }) {
  const { t } = useTranslation();
  const { data: pipelines = [] } = useLeadPipelines();
  const autoSendCount = pipelines.filter(p => p.enabled === 1 && p.autoSend === 1).length;
  const activo = autoSendCount > 0;
  const [saved, markSaved] = useSavedFlag();
  const flush = useDebouncedFlush(() => {
    engine.update.mutate(engine.formRef.current, { onSuccess: markSaved });
  });

  function patch(partial: Partial<typeof engine.form>, immediate = false) {
    engine.setForm(f => ({ ...f, ...partial }));
    if (immediate) setTimeout(() => flush.now(), 0); else flush.queue();
  }

  const horas = Array.from({ length: 24 }, (_, h) => ({ value: h, label: `${String(h).padStart(2, '0')}:00` }));

  return (
    <Card padding="md" {...(activo ? { accent: 'var(--evo-amber)' } : {})} header={
      <>
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-caption)', fontWeight: 'var(--evo-fw-medium)', fontFamily: 'var(--evo-font)', letterSpacing: 'var(--evo-ls-caps)', textTransform: 'uppercase', color: activo ? 'var(--evo-amber)' : 'var(--evo-fg-3)' }}>
          {t('settings.autoTab.cards.limits')}
        </span>
        <SavedChip show={saved} />
        {activo && <StatusPill tone="amber">{t('settings.autoTab.limits.activePill')}</StatusPill>}
      </>
    }>
      <p style={{ margin: '0 0 var(--evo-space-2)', fontSize: 'var(--evo-fs-body)', fontFamily: 'var(--evo-font)', lineHeight: 1.65, color: 'var(--evo-fg-2)' }}>
        {t('settings.autoTab.limits.intro')}
      </p>
      {autoSendCount > 1 && (
        <AmberNotice>{t('settings.pipelines.capWarning', { count: autoSendCount, cap: engine.form.dailyCap })}</AmberNotice>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(var(--evo-related-col-min), 1fr))', gap: '0 var(--evo-space-6)' }}>
        <FieldRow label={t('settings.leadEngine.autoSendDailyCapLabel')} hint={t('settings.leadEngine.autoSendDailyCapHint')} last>
          <span style={{ width: 120 }}>
            <Input type="number" min={0} max={200} value={String(engine.form.dailyCap)}
              onChange={v => patch({ dailyCap: Math.max(0, parseInt(v) || 0) })}
              onBlur={() => flush.now()} size="sm" fullWidth />
          </span>
          <HeadMeta>{t('settings.autoTab.limits.capUnit')}</HeadMeta>
        </FieldRow>
        <FieldRow label={t('settings.leadEngine.autoSendWindowLabel')} hint={t('settings.leadEngine.autoSendWindowHint')} last>
          <Dropdown<number> value={engine.form.autoSendStartHour} options={horas} onChange={v => patch({ autoSendStartHour: v }, true)} size="sm" />
          <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>{t('settings.autoTab.limits.to')}</span>
          <Dropdown<number> value={engine.form.autoSendEndHour} options={horas} onChange={v => patch({ autoSendEndHour: v }, true)} size="sm" />
        </FieldRow>
      </div>
    </Card>
  );
}

/** Lectura de reseñas — sin intentos NO es 0% en rojo: es "no hay tasa". */
interface TelemetrySummary {
  total_attempts: number;
  success_rate: number;
  by_status: Record<string, number>;
}

function ReviewsCard() {
  const { t } = useTranslation();
  const { data } = useQuery<TelemetrySummary>({
    queryKey: ['scraping-telemetry-summary'],
    queryFn: () => api.get<TelemetrySummary>('/api/scraping-telemetry/summary'),
    refetchInterval: 60_000,
  });

  const attempts = data?.total_attempts ?? 0;
  const rate = data?.success_rate ?? 0;
  const sinDatos = attempts === 0;
  const rateColor = rate >= 70 ? 'var(--evo-cyan)' : rate >= 40 ? 'var(--evo-amber)' : 'var(--evo-danger)';

  return (
    <Card padding="md" header={<CardHead title={t('settings.autoTab.cards.reviews')}><HeadMeta>{t('settings.autoTab.reviews.window')}</HeadMeta></CardHead>}>
      <p style={{ margin: '0 0 var(--evo-space-4)', fontSize: 'var(--evo-fs-body)', fontFamily: 'var(--evo-font)', lineHeight: 1.65, color: 'var(--evo-fg-3)' }}>
        {t('settings.autoTab.reviews.intro')}
      </p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--evo-space-3)', marginBottom: 'var(--evo-space-3)' }}>
        <span style={{ fontSize: 34, fontFamily: 'var(--evo-font)', fontWeight: 'var(--evo-fw-medium)', lineHeight: 1, color: sinDatos ? 'var(--evo-fg-4)' : rateColor }}>
          {sinDatos ? '—' : `${rate}%`}
        </span>
        <span style={{ fontSize: 'var(--evo-fs-body)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)' }}>{t('settings.autoTab.reviews.rateLabel')}</span>
      </div>
      <div style={{ height: 4, borderRadius: 'var(--evo-r-pill)', background: 'var(--evo-bg-2)', overflow: 'hidden', marginBottom: 'var(--evo-space-4)' }}>
        <div style={{ width: sinDatos ? '0%' : `${Math.min(100, rate)}%`, height: '100%', background: sinDatos ? 'var(--evo-fg-4)' : rateColor }} />
      </div>
      {sinDatos ? (
        <p style={{ margin: 0, fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', lineHeight: 1.55, color: 'var(--evo-fg-4)' }}>
          {t('settings.autoTab.reviews.noAttempts')}
        </p>
      ) : (
        <p style={{ margin: 0, fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', lineHeight: 1.55, color: 'var(--evo-fg-4)' }}>
          {t('settings.leadEngine.scrapingTelemetry.totalAttempts', { count: attempts })}
        </p>
      )}
    </Card>
  );
}

// ── Pipelines ────────────────────────────────────────────────────────────────

/** Devuelve null si la corrida es de hace menos de un minuto ("ahora"). */
function pipelineRelativeRaw(iso: string): string | null {
  const ms = Date.now() - new Date(iso.endsWith('Z') ? iso : iso + 'Z').getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return null;
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

/** Acordeón de un pipeline — fila del log (diseño) + cuerpo con los cinco
 *  pasos encadenados, horario/última corrida y acciones. Autoguarda. */
function PipelineAccordion({ pipeline, hasOverlap, overlapWith }: {
  pipeline: LeadPipeline; hasOverlap: boolean; overlapWith: string | null;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateLeadPipeline();
  const del = useDeleteLeadPipeline();
  const runNow = useRunPipelineNow();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const [open, setOpen] = useState(false);
  const [saved, markSaved] = useSavedFlag();

  const [form, setForm] = useState(() => ({
    name: pipeline.name,
    enabled: pipeline.enabled === 1,
    categories: pipeline.categories,
    zones: pipeline.zones,
    capPerRun: pipeline.capPerRun,
    cronHour: pipeline.cronHour,
    cronMinute: pipeline.cronMinute,
    intent: ((pipeline.intent as 'audit' | 'demo') || 'audit'),
    autoScrape: pipeline.autoScrape === 1,
    autoEnrich: pipeline.autoEnrich === 1,
    autoAnalysis: pipeline.autoAnalysis === 1,
    autoDraft: pipeline.autoDraft === 1,
    autoSend: pipeline.autoSend === 1,
  }));
  const formRef = useRef(form);
  formRef.current = form;
  const [zoneInput, setZoneInput] = useState('');

  useEffect(() => {
    setForm({
      name: pipeline.name, enabled: pipeline.enabled === 1,
      categories: pipeline.categories, zones: pipeline.zones,
      capPerRun: pipeline.capPerRun, cronHour: pipeline.cronHour, cronMinute: pipeline.cronMinute,
      intent: ((pipeline.intent as 'audit' | 'demo') || 'audit'),
      autoScrape: pipeline.autoScrape === 1, autoEnrich: pipeline.autoEnrich === 1,
      autoAnalysis: pipeline.autoAnalysis === 1, autoDraft: pipeline.autoDraft === 1,
      autoSend: pipeline.autoSend === 1,
    });
  }, [pipeline]);

  const flush = useDebouncedFlush(() => {
    const f = formRef.current;
    const body: LeadPipelineInput = {
      name: f.name, enabled: f.enabled, categories: f.categories, zones: f.zones,
      capPerRun: f.capPerRun, cronHour: f.cronHour, cronMinute: f.cronMinute, intent: f.intent,
      autoScrape: f.autoScrape, autoEnrich: f.autoEnrich, autoAnalysis: f.autoAnalysis,
      autoDraft: f.autoDraft, autoSend: f.autoSend,
    };
    update.mutate({ id: pipeline.id, ...body }, {
      onSuccess: markSaved,
      onError: err => toast.error((err as Error).message),
    });
  });

  function patch(partial: Partial<typeof form>, immediate = false) {
    setForm(f => ({ ...f, ...partial }));
    if (immediate) setTimeout(() => flush.now(), 0); else flush.queue();
  }

  /** Apagar un paso apaga todos los que dependen de él. */
  function setStep(key: 'autoScrape' | 'autoEnrich' | 'autoAnalysis' | 'autoDraft' | 'autoSend', v: boolean) {
    const orden = ['autoScrape', 'autoEnrich', 'autoAnalysis', 'autoDraft', 'autoSend'] as const;
    const idx = orden.indexOf(key);
    const partial: Partial<typeof form> = { [key]: v };
    if (!v) for (const k of orden.slice(idx + 1)) partial[k] = false;
    patch(partial, true);
  }

  async function handleDelete() {
    const ok = await confirmAction({ title: t('settings.pipelines.deleteConfirm'), confirmLabel: t('common.delete'), tone: 'danger' });
    if (!ok) return;
    del.mutate(pipeline.id);
  }

  const isRunning = pipeline.lastRunStatus === 'running' || pipeline.lastRunStatus === 'queued';
  const horaLabel = `${String(form.cronHour).padStart(2, '0')}:${String(form.cronMinute).padStart(2, '0')}`;

  const steps = [
    { n: 1, key: 'autoScrape' as const,   value: form.autoScrape,   locked: false,              requires: 0 },
    { n: 2, key: 'autoEnrich' as const,   value: form.autoEnrich,   locked: !form.autoScrape,   requires: 1 },
    { n: 3, key: 'autoAnalysis' as const, value: form.autoAnalysis, locked: !form.autoEnrich,   requires: 2 },
    { n: 4, key: 'autoDraft' as const,    value: form.autoDraft,    locked: !form.autoAnalysis, requires: 3 },
    { n: 5, key: 'autoSend' as const,     value: form.autoSend,     locked: !form.autoDraft,    requires: 4 },
  ];

  const hourOptions = Array.from({ length: 24 }, (_, i) => ({ value: i, label: `${String(i).padStart(2, '0')}:00` }));
  const minuteOptions = [0, 15, 30, 45].map(m => ({ value: m, label: String(m).padStart(2, '0') }));

  return (
    <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)', overflow: 'hidden' }}>
      {/* Fila colapsada del diseño */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-subrow-gap)', minHeight: 'var(--evo-log-row-min-h)', boxSizing: 'border-box', padding: 'var(--evo-log-row-py) var(--evo-log-row-px)', background: 'var(--evo-log-row-bg)', cursor: 'pointer', userSelect: 'none' }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-log-row-bg-hover)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-log-row-bg)'; }}
      >
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {form.name}
        </span>
        <SavedChip show={saved} />
        {isRunning
          ? <StatusPill tone="info">{t('settings.pipelines.runningBadge')}</StatusPill>
          : <StatusPill tone={form.enabled ? 'cyan' : 'muted'}>{form.enabled ? t('settings.pipelines.active') : t('settings.pipelines.inactive')}</StatusPill>}
        <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', whiteSpace: 'nowrap' }}>
          {t('settings.pipelines.scheduledAt', { time: horaLabel })}
        </span>
        {pipeline.lastRunAt && (
          <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
            {pipelineRelativeRaw(pipeline.lastRunAt) == null ? t('settings.autoTab.pipelines.now') : t('settings.autoTab.pipelines.ago', { time: pipelineRelativeRaw(pipeline.lastRunAt) })}
          </span>
        )}
        <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', flexShrink: 0 }}>
          <Toggle checked={form.enabled} onChange={v => patch({ enabled: v }, true)} size="sm" />
        </span>
        <span onClick={e => { e.stopPropagation(); void handleDelete(); }} title={t('common.delete')}
          style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-danger)', cursor: 'pointer' }}>
          <Icon name="trash" size={13} />
        </span>
        <span style={{ flexShrink: 0, display: 'inline-flex', color: 'var(--evo-fg-4)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <Icon name="chevron-down" size={12} />
        </span>
      </div>

      {open && (
        <div style={{ padding: '0 var(--evo-log-row-px) var(--evo-log-body-py)', boxSizing: 'border-box' }}>
          <div style={{ padding: 'var(--evo-space-4) 0 0', borderTop: '1px solid var(--evo-stroke)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
            {hasOverlap && (
              <AmberNotice>
                {t('settings.autoTab.pipelines.overlap', { other: overlapWith ?? '' })}
              </AmberNotice>
            )}
            {pipeline.exhausted && (
              <AmberNotice>{t('settings.pipelines.exhaustedWarning')}</AmberNotice>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-field-gap)' }}>
              <span style={{ fontSize: 'var(--evo-fs-label)', fontWeight: 'var(--evo-fw-medium)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)' }}>{t('settings.autoTab.pipelines.name')}</span>
              <span style={{ maxWidth: 420, display: 'block' }}>
                <Input value={form.name} onChange={v => patch({ name: v })} onBlur={() => flush.now()} placeholder={t('settings.pipelines.namePlaceholder')} size="sm" fullWidth />
              </span>
            </div>

            {/* Qué busca este pipeline */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(var(--evo-related-col-min), 1fr))', gap: 'var(--evo-space-2) var(--evo-space-6)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-field-gap)' }}>
                <span style={{ fontSize: 'var(--evo-fs-label)', fontWeight: 'var(--evo-fw-medium)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)' }}>{t('settings.scrapeSchedule.categoriesLabel')}</span>
                <Dropdown<string> multi value={form.categories} options={SCRAPE_CATEGORY_OPTIONS}
                  onChange={v => patch({ categories: v }, true)}
                  placeholder={t('settings.scrapeSchedule.categoriesPlaceholder')} size="sm" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-field-gap)' }}>
                <span style={{ fontSize: 'var(--evo-fs-label)', fontWeight: 'var(--evo-fw-medium)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)' }}>{t('settings.scrapeSchedule.zonesLabel')}</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  {form.zones.map((z: ScrapeZone, i: number) => (
                    <span key={i} onClick={() => patch({ zones: form.zones.filter((_, idx) => idx !== i) }, true)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 'var(--evo-pill-h-sm)', boxSizing: 'border-box', padding: '0 var(--evo-pill-px)', fontSize: 'var(--evo-fs-caption)', fontWeight: 'var(--evo-fw-medium)', lineHeight: 1, color: 'var(--evo-fg-2)', background: 'var(--evo-fill-subtle-strong)', border: '1px solid transparent', borderRadius: 'var(--evo-r-pill)', cursor: 'pointer' }}>
                      {z.city}{z.state ? `, ${z.state}` : ''}<Icon name="close" size={9} />
                    </span>
                  ))}
                  <span style={{ width: 160 }}>
                    <Input value={zoneInput} onChange={setZoneInput} size="sm" fullWidth
                      placeholder={t('settings.scrapeSchedule.zonePlaceholder')}
                      onKeyDown={e => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        const trimmed = zoneInput.trim();
                        if (!trimmed) return;
                        const parts = trimmed.split(',').map(p => p.trim());
                        patch({ zones: [...form.zones, { city: parts[0] ?? trimmed, state: parts[1] ?? '' }] }, true);
                        setZoneInput('');
                      }} />
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-field-gap)' }}>
                <span style={{ fontSize: 'var(--evo-fs-label)', fontWeight: 'var(--evo-fw-medium)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)' }}>{t('settings.scrapeSchedule.capLabel')}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
                  <span style={{ width: 90 }}>
                    <Input type="number" value={String(form.capPerRun)}
                      onChange={v => patch({ capPerRun: Math.min(50, Math.max(1, parseInt(v) || 1)) })}
                      onBlur={() => flush.now()} size="sm" fullWidth />
                  </span>
                  <Dropdown<number> value={form.cronHour} options={hourOptions} onChange={v => patch({ cronHour: v }, true)} size="sm" />
                  <Dropdown<number> value={form.cronMinute} options={minuteOptions} onChange={v => patch({ cronMinute: v }, true)} size="sm" />
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-field-gap)' }}>
                <span style={{ fontSize: 'var(--evo-fs-label)', fontWeight: 'var(--evo-fw-medium)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)' }}>{t('settings.scrapeSchedule.intentLabel')}</span>
                <span style={{ display: 'flex', gap: 16 }}>
                  {(['audit', 'demo'] as const).map(mode => (
                    <Radio key={mode} value={mode} checked={form.intent === mode} onChange={() => patch({ intent: mode }, true)}
                      label={t(`settings.scrapeSchedule.intent.${mode}`)} />
                  ))}
                </span>
              </div>
            </div>

            {/* Los cinco pasos encadenados */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', letterSpacing: 'var(--evo-ls-caps)', textTransform: 'uppercase', color: 'var(--evo-fg-4)' }}>
                {t('settings.pipelines.steps.title')}
              </span>
              <p style={{ margin: '0 0 var(--evo-space-2)', fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', lineHeight: 1.55, color: 'var(--evo-fg-3)' }}>
                {t('settings.autoTab.pipelines.chainRule')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {steps.map((s, i) => (
                  <div key={s.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-3)', padding: 'var(--evo-module-row-py) 0', borderBottom: i < steps.length - 1 ? '1px solid var(--evo-stroke)' : 'none', minHeight: 'var(--evo-module-row-min-h)', boxSizing: 'border-box' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', width: 'var(--evo-step-chip-sz)', height: 'var(--evo-step-chip-sz)', marginTop: 1, flexShrink: 0, borderRadius: 'var(--evo-r-pill)', border: `1px solid ${s.value && !s.locked ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`, color: s.value && !s.locked ? 'var(--evo-cyan)' : 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-tiny)', fontWeight: 'var(--evo-fw-medium)', lineHeight: 1 }}>
                      {s.n}
                    </span>
                    <span style={{ paddingTop: 2, flexShrink: 0 }}>
                      <Toggle checked={s.value} disabled={s.locked} onChange={v => setStep(s.key, v)} size="sm" />
                    </span>
                    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)', color: s.locked ? 'var(--evo-fg-3)' : 'var(--evo-fg-1)' }}>
                        {t(`settings.pipelines.steps.${s.key}`)}
                      </span>
                      {/* Un paso bloqueado dice POR QUÉ, en ámbar — no se deja gris sin explicación. */}
                      {s.locked ? (
                        <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-amber)', lineHeight: 1.5 }}>
                          {t('settings.autoTab.pipelines.stepLocked', { n: s.requires })}
                        </span>
                      ) : (
                        <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', lineHeight: 1.5 }}>
                          {t(`settings.pipelines.steps.${s.key.replace('auto', '').toLowerCase()}Hint`, { defaultValue: '' })}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Horario | Última corrida */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(var(--evo-related-col-min), 1fr))', gap: 'var(--evo-space-4) var(--evo-space-6)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
                <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', letterSpacing: 'var(--evo-ls-caps)', textTransform: 'uppercase', color: 'var(--evo-fg-4)' }}>{t('settings.autoTab.pipelines.schedule')}</span>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <KVRight label={t('settings.autoTab.pipelines.runs')} value={t('settings.pipelines.scheduledAt', { time: horaLabel })} />
                  <KVRight label={t('settings.scrapeSchedule.capLabel')} value={`${form.capPerRun}`} last />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
                <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', letterSpacing: 'var(--evo-ls-caps)', textTransform: 'uppercase', color: 'var(--evo-fg-4)' }}>{t('settings.pipelines.lastRun.title')}</span>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <KVRight label={t('settings.autoTab.pipelines.when')} value={pipeline.lastRunAt ? (pipelineRelativeRaw(pipeline.lastRunAt) == null ? t('settings.autoTab.pipelines.now') : t('settings.autoTab.pipelines.ago', { time: pipelineRelativeRaw(pipeline.lastRunAt) })) : t('settings.pipelines.lastRun.never')} />
                  <KVRight
                    label={t('settings.autoTab.pipelines.result')}
                    value={pipeline.lastRunAt ? (
                      <>
                        {pipeline.lastRunCount != null && <span>{t('settings.autoTab.pipelines.leadsCount', { count: pipeline.lastRunCount })}</span>}
                        {pipeline.lastRunStatus && (
                          <StatusPill tone={pipeline.lastRunStatus === 'success' ? 'cyan' : pipeline.lastRunStatus === 'running' || pipeline.lastRunStatus === 'queued' ? 'info' : 'amber'}>
                            {t(`settings.autoTab.pipelines.status.${pipeline.lastRunStatus}`, { defaultValue: pipeline.lastRunStatus })}
                          </StatusPill>
                        )}
                      </>
                    ) : '—'}
                    last
                  />
                </div>
              </div>
            </div>

            {/* Acciones */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
              <Button variant="outline" tone="muted" size="sm" loading={runNow.isPending}
                onClick={() => runNow.mutate(pipeline.id, {
                  onSuccess: data => toast.success(t('settings.pipelines.runNowSuccess', { name: form.name, count: data.runsQueued })),
                  onError: err => toast.error(t('settings.pipelines.runNowError', { message: (err as Error).message })),
                })}>
                {t('settings.pipelines.runNow')}
              </Button>
              <Link to="/leads" style={{ textDecoration: 'none' }}>
                <Button variant="outline" tone="muted" size="sm">{t('settings.autoTab.pipelines.viewLeads')}</Button>
              </Link>
              <Button variant="outline" tone="danger" size="sm" onClick={() => { void handleDelete(); }}>
                {t('settings.autoTab.pipelines.deletePipeline')}
              </Button>
            </div>
          </div>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

function PipelinesCard() {
  const { t } = useTranslation();
  const { data: pipelines = [], isLoading } = useLeadPipelines();
  const create = useCreateLeadPipeline();
  const [showAddModal, setShowAddModal] = useState(false);

  // Solape: pipelines habilitados con el mismo cronHour:cronMinute.
  const byTime = new Map<string, LeadPipeline[]>();
  for (const p of pipelines) {
    if (p.enabled !== 1) continue;
    const key = `${p.cronHour}:${p.cronMinute}`;
    byTime.set(key, [...(byTime.get(key) ?? []), p]);
  }
  const autoSendCount = pipelines.filter(p => p.enabled === 1 && p.autoSend === 1).length;

  return (
    <div id="pipelines-card">
      <Card padding="md" header={
        <CardHead title={t('settings.autoTab.cards.pipelines')}>
          <HeadMeta>{t('settings.autoTab.pipelines.headMeta', { count: pipelines.length, auto: autoSendCount })}</HeadMeta>
          <a href="#" style={{ flexShrink: 0, fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-cyan)', textDecoration: 'none', whiteSpace: 'nowrap' }}
            onClick={e => { e.preventDefault(); setShowAddModal(true); }}>
            + {t('settings.pipelines.addPipeline')}
          </a>
        </CardHead>
      }>
        <p style={{ margin: '0 0 var(--evo-space-4)', fontSize: 'var(--evo-fs-body)', fontFamily: 'var(--evo-font)', lineHeight: 1.65, color: 'var(--evo-fg-3)' }}>
          {t('settings.autoTab.pipelines.intro')}
        </p>
        {isLoading && <LoadingState size="sm" />}
        {!isLoading && pipelines.length === 0 && (
          <EmptyState size="sm" title={t('settings.pipelines.empty')} />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
          {pipelines.map(pipeline => {
            const peers = byTime.get(`${pipeline.cronHour}:${pipeline.cronMinute}`) ?? [];
            const overlap = pipeline.enabled === 1 && peers.length > 1;
            const other = overlap ? (peers.find(p => p.id !== pipeline.id)?.name ?? null) : null;
            return (
              <PipelineAccordion key={pipeline.id} pipeline={pipeline} hasOverlap={overlap} overlapWith={other} />
            );
          })}
        </div>
      </Card>
      {showAddModal && (
        <PipelineEditorModal
          onSave={data => create.mutate(data, { onSuccess: () => setShowAddModal(false) })}
          onCancel={() => setShowAddModal(false)}
          saving={create.isPending}
          error={create.isError ? (create.error as Error).message : null}
        />
      )}
    </div>
  );
}

// ── GRUPO 2 · Vigilancia del correo ──────────────────────────────────────────

type Sensitivity = 'conservative' | 'moderate' | 'aggressive';

function FollowupDetectorCard() {
  const { t } = useTranslation();
  const toast = useToast();
  const [sensitivity, setSensitivity] = useState<Sensitivity>('moderate');
  const [reanalyzeRead, setReanalyzeRead] = useState(false);
  const [saved, markSaved] = useSavedFlag();

  useEffect(() => {
    fetch('/api/settings/key/followup-sensitivity').then(r => r.json()).then((d: { value?: string }) => {
      if (d.value) setSensitivity(d.value as Sensitivity);
    }).catch(() => {});
    fetch('/api/settings/key/reanalyze-read-emails').then(r => r.json()).then((d: { value?: string }) => {
      setReanalyzeRead(d.value === 'true');
    }).catch(() => {});
  }, []);

  function putKey(key: string, value: string) {
    fetch(`/api/settings/key/${key}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ value }),
    })
      .then(r => { if (!r.ok) throw new Error(); markSaved(); })
      .catch(() => toast.error(t('settings.autoTab.detector.saveError')));
  }

  return (
    <Card padding="md" header={<CardHead title={t('settings.autoTab.cards.detector')}><SavedChip show={saved} /></CardHead>}>
      <CardIntro>{t('settings.autoTab.detector.intro')}</CardIntro>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FieldRow label={t('settings.followup.sensitivity.sensitivityLabel')} hint={t('settings.autoTab.detector.sensitivityHint')}>
          <SegmentedControl<Sensitivity>
            value={sensitivity}
            onChange={v => { setSensitivity(v); putKey('followup-sensitivity', v); }}
            size="sm"
            options={[
              { value: 'conservative', label: t('settings.followup.sensitivity.conservative') },
              { value: 'moderate', label: t('settings.followup.sensitivity.moderate') },
              { value: 'aggressive', label: t('settings.followup.sensitivity.aggressive') },
            ]}
          />
        </FieldRow>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-3)', padding: 'var(--evo-field-py) 0' }}>
          <span style={{ paddingTop: 2, flexShrink: 0 }}>
            <Toggle checked={reanalyzeRead} onChange={v => { setReanalyzeRead(v); putKey('reanalyze-read-emails', String(v)); }} size="sm" />
          </span>
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)', color: reanalyzeRead ? 'var(--evo-fg-1)' : 'var(--evo-fg-3)' }}>
              {t('settings.followup.reanalyzeReadLabel')}
            </span>
            <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', lineHeight: 1.5 }}>
              {t('settings.followup.reanalyzeReadDesc')}
            </span>
          </span>
        </div>
      </div>
    </Card>
  );
}

function FollowupRulesCard() {
  const { t } = useTranslation();
  const { data: rules = [], isLoading } = useFollowupRules();
  const deleteRule = useDeleteFollowupRule();

  return (
    <Card padding="md" header={
      <CardHead title={t('settings.autoTab.cards.rules')}>
        <HeadMeta>{t('settings.autoTab.rules.count', { count: rules.length })}</HeadMeta>
      </CardHead>
    }>
      <p style={{ margin: '0 0 var(--evo-space-4)', fontSize: 'var(--evo-fs-body)', fontFamily: 'var(--evo-font)', lineHeight: 1.65, color: 'var(--evo-fg-3)' }}>
        {t('settings.autoTab.rules.intro')}
      </p>
      {isLoading && <LoadingState size="sm" />}
      {!isLoading && rules.length === 0 && (
        <EmptyState size="sm" title={t('settings.followup.noRules')} />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
        {rules.map((rule: FollowupRule) => (
          <SubRow
            key={rule.id}
            title={rule.sender ?? rule.domain ?? '—'}
            meta={`${rule.sender ? t('settings.followup.typeSender') : t('settings.followup.typeDomain')} · ${parseServerDate(rule.createdAt).toLocaleDateString('es', { month: 'short', day: 'numeric' })}`}
            right={
              <>
                <StatusPill tone={rule.action === 'always' ? 'cyan' : 'muted'}>
                  {rule.action === 'always' ? t('settings.followup.actionAlways') : t('settings.followup.actionNever')}
                </StatusPill>
                <span title={t('settings.followup.deleteBtn')}
                  onClick={e => { e.stopPropagation(); deleteRule.mutate(rule.id); }}
                  style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-fg-4)', cursor: 'pointer' }}>
                  <Icon name="close" size={12} />
                </span>
              </>
            }
          />
        ))}
      </div>
    </Card>
  );
}

// ── GRUPO 3 · Evo Brain ──────────────────────────────────────────────────────

const CHANNEL_ICON: Record<string, string> = {
  whatsapp_business: 'social-whatsapp',
  whatsapp: 'social-whatsapp',
  instagram: 'social-instagram',
  facebook: 'social-facebook',
  sms: 'messages',
  email: 'email',
};

function AutonomyCard() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isLoading } = useMessagingAutonomy();
  const setAutonomy = useSetAutonomy();
  const { data: contexto } = useBusinessContext();
  const canales = data?.channels ?? [];
  const [saved, markSaved] = useSavedFlag();

  // Sin guion aprobado, ningún nivel hace nada: el motor no responde sin él.
  // El aviso va ARRIBA de la tarjeta con enlace directo — no una nota al pie.
  const sinGuion = contexto != null && contexto.approved == null;

  const pillDe = (level: AutonomyLevel) =>
    level === 'propose'
      ? <StatusPill tone="cyan">{t('settings.autoTab.autonomy.youReview')}</StatusPill>
      : <StatusPill tone="amber">{t('settings.autoTab.autonomy.sendsAlone')}</StatusPill>;

  return (
    <Card padding="md" header={
      <CardHead title={t('settings.autoTab.cards.autonomy')}>
        <SavedChip show={saved} />
        {sinGuion && <StatusPill tone="amber">{t('settings.autoTab.autonomy.noScriptPill')}</StatusPill>}
      </CardHead>
    }>
      {sinGuion && canales.length > 0 && (
        <AmberNotice action={
          <Link to="/settings?tab=brand" style={{ flexShrink: 0, fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-cyan)', textDecoration: 'none', whiteSpace: 'nowrap' }}>
            {t('settings.autoTab.autonomy.goToBrand')}
          </Link>
        }>
          {t('settings.autonomy.needsScript')}
        </AmberNotice>
      )}
      <CardIntro>{t('settings.autoTab.autonomy.intro')}</CardIntro>
      {isLoading ? (
        <LoadingState size="sm" />
      ) : canales.length === 0 ? (
        <EmptyState size="sm" title={t('settings.autonomy.emptyTitle')} description={t('settings.autonomy.emptyHint')} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {canales.map((c, i) => (
            <div key={c.platform} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)', padding: 'var(--evo-module-row-py) 0', borderBottom: i < canales.length - 1 ? '1px solid var(--evo-stroke)' : 'none', minHeight: 'var(--evo-module-row-min-h)', boxSizing: 'border-box' }}>
              <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-fg-3)' }}>
                <Icon name={(CHANNEL_ICON[c.platform] ?? 'messages') as never} size={14} />
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-1)' }}>
                {t(`messages.channel.${c.platform.replace('_business', '')}`, { defaultValue: c.platform })}
              </span>
              <span style={{ width: 220, flexShrink: 0 }}>
                <Dropdown<string>
                  value={c.level}
                  onChange={v => setAutonomy.mutate(
                    { platform: c.platform, level: v as AutonomyLevel },
                    { onSuccess: markSaved, onError: (err: unknown) => toast.error(err instanceof Error ? err.message : String(err)) },
                  )}
                  options={(data?.levels ?? []).map(l => ({ value: l, label: t(`settings.autonomy.level.${l}`) }))}
                  size="sm"
                />
              </span>
              {pillDe(c.level)}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * Modo del runner de tareas — llegó acá desde el Task Manager (2026-09-02).
 *
 * No es un rótulo: en 'autónomo' el runner toma tareas en estado 'pending'
 * directamente (services/tasks/service.ts, claimNextRunnableTask), es decir
 * EJECUTA sin que nadie apruebe. En 'aprobación' solo toca las ya aprobadas.
 * Vive junto a Autonomía por canal y Proactividad porque es la misma pregunta
 * —cuánto hace solo— aplicada a las tareas, y este grupo es donde se la busca.
 */
function RunnerModeCard() {
  const { t } = useTranslation();
  const { data: runner } = useRunnerStatus();
  const setMode = useSetRunMode();
  const [saved, markSaved] = useSavedFlag();
  const modo = runner?.mode ?? 'approval';

  return (
    <Card padding="md" header={
      <CardHead title={t('settings.autoTab.cards.runnerMode')}>
        <SavedChip show={saved} />
        <StatusPill tone={modo === 'autonomous' ? 'amber' : 'cyan'}>
          {t(`tasks.mode.${modo}`)}
        </StatusPill>
      </CardHead>
    }>
      <CardIntro>{t('settings.autoTab.runnerMode.intro')}</CardIntro>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FieldRow
          label={t('settings.autoTab.runnerMode.label')}
          hint={modo === 'autonomous'
            ? t('settings.autoTab.runnerMode.hintAutonomous')
            : t('settings.autoTab.runnerMode.hintApproval')}
          last
        >
          <SegmentedControl<'approval' | 'autonomous'>
            value={modo}
            onChange={m => setMode.mutate(m, { onSuccess: markSaved })}
            size="sm"
            options={[
              { value: 'approval', label: t('tasks.mode.approval') },
              { value: 'autonomous', label: t('tasks.mode.autonomous') },
            ]}
          />
        </FieldRow>
      </div>
    </Card>
  );
}

function ProactivityCard() {
  const { t } = useTranslation();
  const { data: settings } = useAllSettings();
  const updateSetting = useUpdateSetting();
  const [saved, markSaved] = useSavedFlag();

  const enabled = (settings?.['proactive-enabled'] ?? 'true') === 'true';
  const vals = {
    daysDrafted: settings?.['proactive-days-drafted'] ?? '3',
    daysOverdue: settings?.['proactive-days-invoice-overdue'] ?? '3',
    hoursWindow: settings?.['proactive-hours-window-closing'] ?? '4',
    maxPerRun: settings?.['proactive-max-per-run'] ?? '3',
  };
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  function saveInt(key: string, raw: string, fallback: string) {
    const n = parseInt(raw || fallback, 10);
    if (isNaN(n) || n < 1) return;
    updateSetting.mutate({ key, value: String(n) }, { onSuccess: markSaved });
  }

  // Apagada: los parámetros se VEN en fg-4 pero no se ocultan ni se apilan
  // atenuaciones (fg-4 ya es el token de inerte — sin opacity encima).
  const numRow = (label: string, key: string, current: string, unit: string, last?: boolean) => (
    <KVRight
      label={label}
      dim={!enabled}
      value={enabled ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <span style={{ width: 80 }}>
            <Input type="number" min={1} value={drafts[key] ?? current}
              onChange={v => setDrafts(d => ({ ...d, [key]: v }))}
              onBlur={() => { if (drafts[key] != null && drafts[key] !== current) saveInt(key, drafts[key]!, current); }}
              size="sm" fullWidth />
          </span>
          <HeadMeta>{unit}</HeadMeta>
        </span>
      ) : (
        `${current} ${unit}`
      )}
      {...(last ? { last: true } : {})}
    />
  );

  return (
    <Card padding="md" header={
      <CardHead title={t('settings.autoTab.cards.proactivity')}>
        <SavedChip show={saved} />
        <StatusPill tone={enabled ? 'cyan' : 'muted'}>
          {enabled ? t('settings.autoTab.proactivity.onPill') : t('settings.autoTab.proactivity.offPill')}
        </StatusPill>
      </CardHead>
    }>
      <CardIntro>{t('settings.autoTab.proactivity.intro')}</CardIntro>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-3)', padding: 'var(--evo-field-py) 0', borderBottom: '1px solid var(--evo-stroke)' }}>
        <span style={{ paddingTop: 2, flexShrink: 0 }}>
          <Toggle checked={enabled} onChange={v => updateSetting.mutate({ key: 'proactive-enabled', value: v ? 'true' : 'false' }, { onSuccess: markSaved })} size="sm" />
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)', color: enabled ? 'var(--evo-fg-1)' : 'var(--evo-fg-3)' }}>
            {t('settings.evobrain.proactiveEnabledLabel')}
          </span>
          <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', lineHeight: 1.5 }}>
            {enabled ? t('settings.evobrain.proactiveEnabledHint') : t('settings.autoTab.proactivity.offHint')}
          </span>
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {numRow(t('settings.evobrain.daysDraftedLabel'), 'proactive-days-drafted', vals.daysDrafted, t('settings.leadEngine.days'))}
        {numRow(t('settings.evobrain.daysOverdueLabel'), 'proactive-days-invoice-overdue', vals.daysOverdue, t('settings.leadEngine.days'))}
        {numRow(t('settings.evobrain.hoursWindowLabel'), 'proactive-hours-window-closing', vals.hoursWindow, t('settings.autoTab.proactivity.hours'))}
        {numRow(t('settings.evobrain.maxPerRunLabel'), 'proactive-max-per-run', vals.maxPerRun, t('settings.autoTab.proactivity.perRun'), true)}
      </div>
    </Card>
  );
}

function LanguageToolsCard() {
  const { t } = useTranslation();
  const { data: settings } = useAllSettings();
  const updateSetting = useUpdateSetting();
  const { data, isLoading } = useToolApprovals();
  const revoke = useRevokeToolApproval();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const approvals = data?.approvals ?? [];
  const [saved, markSaved] = useSavedFlag();

  async function handleRevoke(toolName: string, label: string, scope: string) {
    const ok = await confirmAction({ title: t('settings.evobrain.revoke_confirm', { label }), tone: 'danger' });
    if (!ok) return;
    revoke.mutate(scope ? { toolName, scope } : { toolName });
  }

  // Una herramienta puede tener varias exenciones, una por tipo de pedido
  // (capa 3). El tipo va en el título para que dos filas de la misma
  // herramienta se distingan — y se revoquen — por separado.
  function approvalTitle(a: { label: string; scope: string }): string {
    if (!a.scope) return a.label;
    return `${a.label} · ${t(`settings.evobrain.scope.${a.scope}`, { defaultValue: a.scope })}`;
  }

  return (
    <Card padding="md" header={
      <CardHead title={t('settings.autoTab.cards.languageTools')}>
        <SavedChip show={saved} />
        <HeadMeta>{t('settings.autoTab.tools.count', { count: approvals.length })}</HeadMeta>
      </CardHead>
    }>
      {confirmDialog}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FieldRow label={t('settings.autoTab.language.label')} hint={t('settings.autoTab.language.hint')} last>
          <span style={{ width: 200 }}>
            <Dropdown<string>
              value={settings?.['assistant_language'] ?? 'follow'}
              options={[
                { value: 'follow', label: t('settings.evobrain.languageFollow') },
                { value: 'es', label: t('settings.generalSection.langEs') },
                { value: 'en', label: t('settings.generalSection.langEn') },
              ]}
              onChange={v => updateSetting.mutate({ key: 'assistant_language', value: v }, { onSuccess: markSaved })}
              size="sm"
            />
          </span>
        </FieldRow>
      </div>
      <div style={{ marginTop: 'var(--evo-space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
        <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', letterSpacing: 'var(--evo-ls-caps)', textTransform: 'uppercase', color: 'var(--evo-fg-4)' }}>
          {t('settings.autoTab.tools.title')}
        </span>
        {isLoading && <LoadingState size="sm" />}
        {!isLoading && approvals.length === 0 && (
          <EmptyState size="sm" title={t('settings.evobrain.empty_title')} description={t('settings.evobrain.empty_description')} />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
          {approvals.map(a => (
            <SubRow
              key={a.id}
              title={approvalTitle(a)}
              meta={t('settings.evobrain.granted_at', { date: parseServerDate(a.grantedAt).toLocaleDateString('es', { day: 'numeric', month: 'short' }) })}
              right={
                <span title={t('settings.evobrain.revoke')}
                  onClick={e => { e.stopPropagation(); void handleRevoke(a.toolName, approvalTitle(a), a.scope); }}
                  style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-fg-4)', cursor: 'pointer' }}>
                  <Icon name="close" size={12} />
                </span>
              }
            />
          ))}
        </div>
      </div>
    </Card>
  );
}

// ── Pestaña ──────────────────────────────────────────────────────────────────

export function AutomationTab({ can }: { can: { leads: boolean; email: boolean; messaging: boolean } }) {
  const { t } = useTranslation();
  const engine = useLeadEngineForm();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-related-gap)' }}>
      {/* Autoguardado: la pestaña lo dice UNA vez, cada tarjeta lo confirma. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)' }}>
        <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-fg-4)' }}><Icon name="check" size={12} /></span>
        {t('settings.autoTab.autosaveNote')}
      </div>

      {can.leads && (
        <>
          <GroupLabel first>{t('settings.autoTab.groups.prospects')}</GroupLabel>
          <div style={grid2}>
            <ProspectSearchCard />
            <SequenceCard engine={engine} />
          </div>
          <PipelinesCard />
          <div style={grid2}>
            <AutoSendLimitsCard engine={engine} />
            <ReviewsCard />
          </div>
        </>
      )}

      {can.email && (
        <>
          <GroupLabel>{t('settings.autoTab.groups.mail')}</GroupLabel>
          <div style={grid2}>
            <FollowupDetectorCard />
            <FollowupRulesCard />
          </div>
        </>
      )}

      <GroupLabel>{t('settings.autoTab.groups.brain')}</GroupLabel>
      {can.messaging && <AutonomyCard />}
      <RunnerModeCard />
      <div style={grid2}>
        <ProactivityCard />
        <LanguageToolsCard />
      </div>
    </div>
  );
}
